import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { isUnauthorizedError } from '../lib/httpUtils';
import { describeAutoVerifyNotification } from '../lib/autoVerifyNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppHeader = ({
  title,
  isAdmin = false,
  onShowScanner,
  onShowEarnings,
  onShowBacktest,
  onShowAdmin,
  onLogout,
  notifications = [],
  onOpenNotification,
  onClearNotifications,
}) => {
  const { t } = useTranslation();
  const initialUsername = typeof window !== 'undefined' ? window.localStorage.getItem('username') : '';
  const initialFirstName = typeof window !== 'undefined' ? window.localStorage.getItem('first_name') : '';
  const initialLastName = typeof window !== 'undefined' ? window.localStorage.getItem('last_name') : '';
  const [username, setUsername] = useState(initialUsername || '');
  const [firstName, setFirstName] = useState(initialFirstName || initialUsername || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;
  const actionsMenuRef = useRef(null);
  const [systemSettings, setSystemSettings] = useState({
    company_name: 'Company Name',
    logo_data: null,
    updated_at: null,
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    companyName: 'Company Name',
    logoData: null,
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const mobileActionsRef = useRef(null);
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
        if (!token) return;

        const response = await axios.get(`${API}/auth/user-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const fetchedFirstName = response.data?.first_name || '';
        const fetchedLastName = response.data?.last_name || '';
        const fetchedUsername = response.data?.username || '';

        if (fetchedUsername) {
          setUsername(fetchedUsername);
        }
        if (fetchedFirstName || fetchedUsername) {
          setFirstName(fetchedFirstName || fetchedUsername);
        }
        if (fetchedLastName) {
          setLastName(fetchedLastName);
        }

        try {
          if (typeof window !== 'undefined') {
            if (fetchedUsername) window.localStorage.setItem('username', fetchedUsername);
            if (fetchedFirstName) window.localStorage.setItem('first_name', fetchedFirstName);
            if (fetchedLastName) window.localStorage.setItem('last_name', fetchedLastName);
          }
        } catch (storageError) {
          /* noop */
        }
      } catch (err) {
        if (isUnauthorizedError(err)) {
          onLogout?.();
          return;
        }
        console.error('Failed to load user profile:', err);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
        if (!token) return;

        const response = await axios.get(`${API}/settings/system`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response?.data) {
          setSystemSettings({
            company_name: response.data.company_name || 'Company Name',
            logo_data: response.data.logo_data || null,
            updated_at: response.data.updated_at || null,
          });
        }
      } catch (err) {
        if (isUnauthorizedError(err)) {
          onLogout?.();
          return;
        }
        console.error('Failed to load system settings:', err);
      }
    };

    fetchSystemSettings();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setActionsMenuOpen(false);
      }
      if (mobileActionsRef.current && !mobileActionsRef.current.contains(event.target)) {
        setMobileActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [systemSettings, title, isAdmin, actionsMenuOpen, settingsModalOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    document.documentElement.style.setProperty('--app-header-height', `${headerHeight}px`);
  }, [headerHeight]);

  const toggleActionsMenu = () => {
    setActionsMenuOpen((prev) => !prev);
  };

  const toggleMobileActions = () => {
    setMobileActionsOpen((prev) => !prev);
  };

  const openUserSettings = () => {
    setActionsMenuOpen(false);
    setUserSettingsOpen(true);
  };

  const handleMenuSelect = (callback) => {
    setActionsMenuOpen(false);
    if (typeof callback === 'function') {
      callback();
    }
  };

  const openSystemSettingsModal = () => {
    setSettingsForm({
      companyName: systemSettings?.company_name || 'Company Name',
      logoData: systemSettings?.logo_data || null,
    });
    setSettingsError('');
    setSettingsModalOpen(true);
  };

  const handleLogoFileChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setSettingsError('Logo must be 2MB or less');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSettingsForm((prev) => ({
        ...prev,
        logoData: reader.result,
      }));
    };
    reader.onerror = () => {
      setSettingsError('Failed to read logo file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setSettingsForm((prev) => ({ ...prev, logoData: null }));
  };

  const handleSaveSystemSettings = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    try {
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
      if (!token) throw new Error('Missing authentication token');

      const payload = {
        company_name: settingsForm.companyName?.trim() || 'Company Name',
        logo_data: settingsForm.logoData === null ? '' : settingsForm.logoData,
      };

      const response = await axios.put(`${API}/settings/system`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response?.data) {
        setSystemSettings({
          company_name: response.data.company_name || 'Company Name',
          logo_data: response.data.logo_data || null,
          updated_at: response.data.updated_at || null,
        });
      }

      setSettingsModalOpen(false);
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to save settings';
      setSettingsError(message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const firstInitial = firstName?.trim()?.charAt(0) || '';
  const lastInitial = lastName?.trim()?.charAt(0) || '';
  let combinedInitials = `${firstInitial}${lastInitial}`.toUpperCase();
  if (!combinedInitials.trim()) {
    combinedInitials = username?.trim()?.charAt(0)?.toUpperCase() || '?';
  }

  const companyName = systemSettings?.company_name || 'Company Name';
  const companyLogo = systemSettings?.logo_data || null;
  const logoElement = companyLogo ? (
    <img
      src={companyLogo}
      alt={companyName}
      className="h-10 w-10 object-contain"
    />
  ) : (
    <span className="text-lg font-semibold text-white">
      {companyName?.slice(0, 2)?.toUpperCase() || 'CN'}
    </span>
  );

  return (
    <>
      <div ref={headerRef} className="bg-[#1c1e21] shadow-lg border-b border-[#2a2d32] fixed top-0 left-0 right-0 z-50">
        <div className="px-4 sm:px-6 py-2 flex flex-col gap-3">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-3 text-white">
              <div
                className="flex items-center justify-center rounded-md bg-white/5 border border-white/15 p-1.5"
                title={companyName}
              >
                {logoElement}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setBellOpen(open => !open)}
                    className="relative w-10 h-10 flex items-center justify-center rounded-md bg-white/10 text-white border border-white/20 hover:bg-white/20 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    aria-label="Notifications"
                    aria-haspopup="true"
                    aria-expanded={bellOpen}
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute -right-1 -top-1 min-w-[1.1rem] rounded-full bg-red-500 px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {bellOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setBellOpen(false)} />
                      <div className="fixed inset-x-2 top-16 z-20 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                          <span className="text-sm font-semibold">Notifications</span>
                          {notifications.length > 0 && (
                            <button
                              type="button"
                              onClick={() => onClearNotifications && onClearNotifications()}
                              className="text-xs text-gray-500 hover:text-gray-800"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                        {notifications.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-gray-500">No notifications</p>
                        ) : (
                          <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
                            {notifications.map(n => (
                              <li key={n.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBellOpen(false);
                                    onOpenNotification && onOpenNotification(n);
                                  }}
                                  className={`w-full px-3 py-2 text-left hover:bg-gray-50 ${n.read ? '' : 'bg-blue-50/60'}`}
                                >
                                  {n.type === 'scan' ? (
                                    <>
                                      <p className="text-sm font-medium text-gray-900">Scan completed</p>
                                      <p className="text-xs text-gray-500">{n.source === 'automatic' ? `Auto Scanner: ${n.name}` : 'Manual scanner'} · {(n.findings || []).reduce((total, result) => total + (result.resistance_levels?.length || 0), 0)} resistance levels found</p>
                                      <p className="text-xs text-gray-500">
                                        {n.time.toLocaleTimeString()} · {n.scanned} scanned
                                        {n.signals.length === 0 ? ' · No signals found' : ` · Found ${n.signals.length} signal${n.signals.length === 1 ? '' : 's'}`}
                                      </p>
                                      {n.existing_predictions > 0 && (
                                        <p className="text-xs text-gray-500">{t('scan_predictions_exist', { count: n.existing_predictions })}</p>
                                      )}
                                    </>
                                  ) : (
                                  <>
                                  <p className="text-sm font-medium text-gray-900">
                                    {n.outcome === 'error' ? 'Auto-verify failed' : n.outcome === 'idle' ? 'Auto-verify ran' : 'Auto-verify completed'}
                                  </p>
                                  <p className={`text-xs ${n.outcome === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
                                    {n.time.toLocaleTimeString()} · {describeAutoVerifyNotification(n)}
                                  </p>
                                  {(n.changes || []).slice(0, 3).map(c => (
                                    <p key={c.id} className="truncate text-xs text-gray-700">
                                      <span className="font-semibold">{c.symbol}</span> {c.from} → {c.to}
                                    </p>
                                  ))}
                                  {(n.changes || []).length > 3 && (
                                    <p className="text-xs text-gray-400">+{n.changes.length - 3} more</p>
                                  )}
                                  </>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="relative" ref={mobileActionsRef}>
                  <button
                    type="button"
                    onClick={toggleMobileActions}
                    className="w-10 h-10 flex items-center justify-center rounded-md bg-white/10 text-white border border-white/20 hover:bg-white/20 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    aria-label={t('actions')}
                    aria-haspopup="true"
                    aria-expanded={mobileActionsOpen}
                  >
                    <span className="text-lg" aria-hidden="true">☰</span>
                  </button>
                  {mobileActionsOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-20 origin-top"
                      style={{ maxHeight: 'calc(100vh - 5rem)', overflowY: 'auto' }}
                    >
                      {typeof onShowScanner === 'function' && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileActionsOpen(false);
                            onShowScanner();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          📊 {t('resistance_scanner_label')}
                        </button>
                      )}
                      {typeof onShowEarnings === 'function' && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileActionsOpen(false);
                            onShowEarnings();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          💹 {t('earnings_label')}
                        </button>
                      )}
                      {isAdmin && typeof onShowBacktest === 'function' && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileActionsOpen(false);
                            onShowBacktest();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          📈 {t('backtest_metrics_label')}
                        </button>
                      )}
                      {isAdmin && typeof onShowAdmin === 'function' && (
                        <button
                          type="button"
                          onClick={() => {
                            setMobileActionsOpen(false);
                            onShowAdmin();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          🛡️ {t('admin')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="relative flex-shrink-0" ref={actionsMenuRef}>
                  <button
                    type="button"
                    onClick={toggleActionsMenu}
                    className="w-10 h-10 sm:w-11 sm:h-11 bg-white/10 text-white rounded-full hover:bg-white/20 transition text-sm border border-white/25 flex items-center justify-center font-semibold shadow-sm"
                    aria-haspopup="true"
                    aria-expanded={actionsMenuOpen}
                    aria-label={t('actions')}
                  >
                    {combinedInitials}
                  </button>
                  {actionsMenuOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-20">
                      <button
                        type="button"
                        onClick={openUserSettings}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        🧩 My Settings
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleMenuSelect(openSystemSettingsModal)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          ⚙️ {t('system_settings')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleMenuSelect(onLogout)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        ↩️ {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {title && typeof title !== 'string' && (
              <div>{title}</div>
            )}
            {title && typeof title === 'string' && (
              <h1 className="text-xl sm:text-2xl font-bold text-white">{title}</h1>
            )}
          </div>
        </div>
      </div>
    {isAdmin && settingsModalOpen && (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 px-4 py-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">System Settings</h3>
              <p className="text-sm text-gray-500">Update the company logo and name shown in the header.</p>
            </div>
            <button
              type="button"
              onClick={() => setSettingsModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="px-5 py-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="company-name-input">
                Company Name
              </label>
              <input
                id="company-name-input"
                type="text"
                maxLength={150}
                value={settingsForm.companyName}
                onChange={(e) => setSettingsForm((prev) => ({ ...prev, companyName: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter company name"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Company Logo</p>
                  <p className="text-xs text-gray-500">PNG or JPG, up to 2MB. Square images look best.</p>
                </div>
                {settingsForm.logoData && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove logo
                  </button>
                )}
              </div>
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  className="cursor-pointer text-sm"
                />
                {settingsForm.logoData ? (
                  <div className="mt-4 flex flex-col items-center gap-2">
                    <img src={settingsForm.logoData} alt="Logo preview" className="h-16 w-16 object-contain" />
                    <span className="text-xs text-gray-500">Preview</span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">No logo uploaded</p>
                )}
              </div>
            </div>

            {settingsError && (
              <p className="text-sm text-red-600">{settingsError}</p>
            )}
          </div>
          <div className="bg-gray-50 px-5 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setSettingsModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveSystemSettings}
              disabled={settingsSaving}
              className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-60"
            >
              {settingsSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    )}
    {userSettingsOpen && (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 py-6">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">My Settings</h3>
              <p className="text-sm text-gray-500">Update your language preference.</p>
            </div>
            <button
              type="button"
              onClick={() => setUserSettingsOpen(false)}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="px-6 py-6 space-y-4 max-h-[82vh] overflow-y-auto">
            <p className="text-sm text-gray-600">Select your preferred language below.</p>
            <div className="w-full max-w-lg mx-auto">
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <span className="text-base">🌐</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Language</p>
                    <p className="text-xs text-gray-500">Instantly updates your app experience.</p>
                  </div>
                </div>
                <div className="p-4">
                  <LanguageSelector />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-4 flex justify-end">
            <button
              type="button"
              onClick={() => setUserSettingsOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
      {/* Spacer preserves layout height for the fixed header */}
      <div style={{ height: headerHeight }} />
    </>
  );
};

export default AppHeader;
